import { Accordion, Alert, Anchor, Table, Title, Text, Group, ThemeIcon, Badge, SimpleGrid, Paper, Space, Tooltip, Collapse, Card, Button, Menu, Stack, ActionIcon } from '@mantine/core';
import React, { ReactNode, useContext, useRef, useState, useEffect, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { CompanyAuthorizedSignatories, CompanyRegistryActivity, CompanyRegistryAddress, CompanyRegistryBeneficiary, CompanyRegistryCustomerRefreshRequest, CompanyRegistryCustomerRefreshResult, CompanyRegistryData, CompanyRegistryLicense, ConfigDataType, CustomClaims, Customer, CustomerFundData, CustomerFundDataTransaction, CustomerSummaryDoc, CustomerSummaryType, Fund, KfhTradeAccount, Order, OrderStatusCode, Portfolio, Position } from '../../types';
import { IconAlertCircle, IconBuildingBank, IconCash, IconChartLine, IconChevronDown, IconEdit, IconFileDownload, IconFolder, IconGlobe, IconMapPin, IconPlus, IconRefresh, IconTrash, IconUsers } from '@tabler/icons-react';
import { CustomInfoCard } from './CustomInfoCard';
import NumberDisplay from '../../components/NumberDisplay';
import FundTransferDetails, { isFundTransfer } from '../../components/FundTransferDetails';
import { ConfigContext, GlobalDataContext, MyUserDataContext, PresenceContext } from '../../contexts';
import { FundSummarizedData } from '../../components/fund_summarized_data';
import AskForConsent from '../../components/ask_for_consent';
import { PieChart, PieValueType, useDrawingArea } from '@mui/x-charts';
import { styled } from '@mui/material/styles';
import { dateFormater, toKwdInt } from '../../helpers/common';
import { canViewKfhTradeAccounts, convertKfhTradeAccountsToPortfolio, hasKFHTradeBalance } from '../../helpers/customerDetailsHelpers';
import { TradeAccounts } from './trade_accounts';
import GenevaReportLauncherButton, { type GenevaReportFieldDefinition, type GenevaReportFieldValues, type GenevaReportLauncherHandle } from '../../components/GenevaReportLauncherButton';
import { useFunctions, useStorage } from '../../helpers/firebaseContext';

import { Timestamp, doc, getDoc } from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';
import { useDisclosure } from "@mantine/hooks";
import { SignatoryModal } from '../../components/signatory_modal';
import { useFirestore } from '../../helpers/firebaseContext';
import { useAuthorizedSignatories } from '../../hooks/useAuthorizedSignatories';
import { InvestmentPerformanceProvider } from '../../hooks/useInvestmentPerformance';
import { canReadInvestmentPerformance, InvestmentPerformanceTarget } from '../../helpers/investmentPerformance';
import InvestmentPerformancePanel, { TotalValueSparkline } from '../../components/InvestmentPerformancePanel';
import { isLegacyOrderAutoDone } from '../../../../shared/legacyOrderStatus';


export interface CustomerInfoProps {
  fundData: CustomerFundData[];
  portfoliosData: Portfolio[];
  kfhTradeAccountsData: KfhTradeAccount[];
  customerSummariesData?: CustomerSummaryDoc[];
  customerData: Customer;
  activePortfolioCode?: string | null;
  pendingOrders?: Order[];
}

const REPORT_PERIOD_END_DATE = '2037/01/01 00:00:00';

// Feature flag: whether KFH RM users can see the "Performance Metrics and Income"
// card on portfolios. While disabled, RM users see a "not available, coming soon"
// placeholder instead. Flip this to true to enable the real metrics for RM.
const show_performance_for_rm = false;


const getDefaultReportStartDate = (): Date => {
  const date = new Date();
  date.setDate(1);
  date.setHours(0, 0, 0, 0);
  return date;
}

const getDefaultReportEndDate = (): Date => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
}

const formatGenevaDateTime = (date: Date, endOfDay = false): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}/${month}/${day} ${endOfDay ? '23:59:59' : '00:00:00'}`;
}

const formatGenevaTimestamp = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');
  const seconds = `${date.getSeconds()}`.padStart(2, '0');
  return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
}

const getTodayEndOfDay = (): string => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return formatGenevaDateTime(date, true);
}

const getCurrentKnowledgeDateTime = (): string => {
  return formatGenevaTimestamp(new Date());
}

const getDateFieldValue = (values: GenevaReportFieldValues, key: string): Date | null => {
  const value = values[key];
  return value instanceof Date ? value : null;
}

const portfolioStatementFields: GenevaReportFieldDefinition[] = [
  {
    key: 'startDate',
    label: 'Start date',
    type: 'date',
    required: true,
    defaultValue: () => getDefaultReportStartDate(),
    maxDate: ({ values }) => getDateFieldValue(values, 'endDate') ?? undefined,
  },
  {
    key: 'endDate',
    label: 'End date',
    type: 'date',
    required: true,
    defaultValue: () => getDefaultReportEndDate(),
    minDate: ({ values }) => getDateFieldValue(values, 'startDate') ?? undefined,
    maxDate: 'today',
  },
];

const realizedProfitLossFields: GenevaReportFieldDefinition[] = [
  {
    key: 'startDate',
    label: 'Start date',
    type: 'date',
    required: true,
    defaultValue: () => getDefaultReportStartDate(),
    maxDate: ({ values }) => getDateFieldValue(values, 'endDate') ?? undefined,
  },
  {
    key: 'endDate',
    label: 'End date',
    type: 'date',
    required: true,
    defaultValue: () => getDefaultReportEndDate(),
    minDate: ({ values }) => getDateFieldValue(values, 'startDate') ?? undefined,
    maxDate: 'today',
  },
];

// Interface for grouped positions
interface GroupedPosition {
  exchange: string;
  currency: string;
  title: string;
  list: Position[];
  combinedPosition: Position;
}

interface PendingFundOrderGroup {
  fundCode: string;
  fundName: string;
  fund?: Fund;
  currency: string;
  orders: Order[];
  pendingAmount: number;
  pendingUnits: number;
}

// `done` is treated as pending so a completed subscription whose position
// hasn't synced into fund_data yet still shows on the positions tables.
const PENDING_POSITION_STATUSES = new Set<string>([
  OrderStatusCode.waiting_for_nav,
  OrderStatusCode.processing,
  OrderStatusCode.done,
]);

const getOrderFundCode = (order: Order) => `${order.fund?.code || order.fund?.id || ''}`.trim();

const getOrderFundName = (order: Order) => order.fund?.name_en || order.fund?.name || order.fund?.short_name || getOrderFundCode(order);

const getPendingOrderAmount = (order: Order) => finiteFundNumberOrZero(order.postNavAmount ?? order.transferAmount);

const getPendingOrderUnits = (order: Order) => finiteFundNumberOrZero(order.estimated_units ?? order.units);

const getPendingOrderCurrency = (order: Order) => order.fund?.currency || 'KWD';

const isPendingSubscriptionOrder = (order: Order) =>
  order.type === 'subscription'
  && PENDING_POSITION_STATUSES.has(order.tags?.orderStatus?.code || '')
  && getOrderFundCode(order) !== '';

const getPendingOrderExpectedNavDate = (order: Order) => getTimestampDate(order.expected_nav_date);

const getResolvedOrderFund = (order: Order, fundByCode: { [key: string]: Fund }) => {
  const fundCode = getOrderFundCode(order);
  return fundByCode[fundCode] || order.fund;
};

// Same rule the backend uses for order status, but only for funds with their own cutoff.
const isBeforeBurganCutoff = (order: Order, fundByCode: { [key: string]: Fund }) => {
  const fund = getResolvedOrderFund(order, fundByCode);
  return !!fund?.burgan_cutoff_date && isLegacyOrderAutoDone({ ...order, fund, burgan_cutoff_date: fund.burgan_cutoff_date });
};

const shouldShowPendingSubscriptionOrder = (order: Order, fundByCode: { [key: string]: Fund }) =>
  isPendingSubscriptionOrder(order) && !isBeforeBurganCutoff(order, fundByCode);

const formatOrderExpectedNavDate = (order: Order) => {
  const expectedNavDate = getPendingOrderExpectedNavDate(order);
  return expectedNavDate ? dateFormater(expectedNavDate) : '-';
};

const formatPendingExpectedNavDates = (orders: Order[]) => {
  const uniqueDates = Array.from(new Map(
    orders
      .map(getPendingOrderExpectedNavDate)
      .filter((date): date is Date => !!date)
      .sort((a, b) => a.getTime() - b.getTime())
      .map((date) => [date.toISOString().slice(0, 10), date])
  ).values());

  if (uniqueDates.length === 0) {
    return 'Expected NAV: -';
  }

  return `Expected NAV: ${uniqueDates.map((date) => dateFormater(date)).join(', ')}`;
};

// Open-ended `done` orders are never shown as pending — their subscription is
// uploaded manually into fund_data and would show twice. Close-ended keeps
// showing `done` until the position syncs.
const isDoneOpenEndedOrder = (order: Order, fundByCode: { [key: string]: Fund }) =>
  order.tags?.orderStatus?.code === OrderStatusCode.done
  && getResolvedOrderFund(order, fundByCode)?.type === 'open_ended';

const buildPendingFundOrderGroups = (orders: Order[], fundByCode: { [key: string]: Fund }) => {
  const groups: Record<string, PendingFundOrderGroup> = {};

  orders.filter((order) => shouldShowPendingSubscriptionOrder(order, fundByCode) && !isDoneOpenEndedOrder(order, fundByCode)).forEach((order) => {
    const fundCode = getOrderFundCode(order);
    const fund = getResolvedOrderFund(order, fundByCode);
    if (!groups[fundCode]) {
      groups[fundCode] = {
        fundCode,
        fundName: fund?.name_en || fund?.name || getOrderFundName(order),
        fund,
        currency: fund?.currency || getPendingOrderCurrency(order),
        orders: [],
        pendingAmount: 0,
        pendingUnits: 0,
      };
    }

    groups[fundCode].orders.push(order);
    groups[fundCode].pendingAmount += getPendingOrderAmount(order);
    groups[fundCode].pendingUnits += getPendingOrderUnits(order);
  });

  return groups;
};

const getPendingGroupFundType = (group: PendingFundOrderGroup): Fund['type'] | undefined => group.fund?.type || group.orders[0]?.fund?.type;

// Confirmation letters are self-sufficient Storage objects at a deterministic
// path (uploaded by utils/uploadFundConfirmationLetters.cjs) — no Firestore
// record exists, so presence is detected by resolving the download URL.
const FundConfirmationLetterLink = ({ customerId, fundId }: { customerId?: string, fundId?: string }) => {
  const storage = useStorage();
  const appContext = useContext(MyUserDataContext);
  const userClaims = appContext.firebaseTokenResult?.claims.customClaims as CustomClaims;
  // Same audience as KFH Trade accounts (admin / CR) — KFH RM must not see it.
  const canView = canViewKfhTradeAccounts(userClaims);
  const [url, setUrl] = useState<string>();

  useEffect(() => {
    setUrl(undefined);
    if (!canView || !customerId || !fundId) return;
    getDownloadURL(ref(storage, `kyc/${customerId}/fund_confirmation_letter_${fundId}.pdf`))
      .then(setUrl)
      .catch(() => { /* no letter uploaded for this fund */ });
  }, [storage, customerId, fundId, canView]);

  if (!url) return null;

  return (
    <Anchor href={url} target="_blank" rel="noreferrer" size="xs">
      <Group gap={4} justify="center" wrap="nowrap">
        <IconFileDownload size={14} />
        Confirmation Letter
      </Group>
    </Anchor>
  );
};

const PendingPositionMarker = ({ group }: { group: PendingFundOrderGroup }) => {
  const orderCountLabel = group.orders.length === 1 ? '1 order' : `${group.orders.length} orders`;

  return (
    <Stack gap={3} align="center" mt={4}>
      <Group gap={4} justify="center">
        <Tooltip label={`Pending ${orderCountLabel}: ${group.orders.map((order) => order.tags?.orderStatus?.title || order.tags?.orderStatus?.code || 'Processing').join(', ')}`}>
          <Badge color="indigo" variant="light">Processing</Badge>
        </Tooltip>
        {group.orders.length > 1 && <Badge color="gray" variant="outline">{orderCountLabel}</Badge>}
      </Group>
      <Text size="xs" c="dimmed">{formatPendingExpectedNavDates(group.orders)}</Text>
    </Stack>
  );
};

const displayValue = (value: unknown): ReactNode => {
  if (value instanceof Timestamp) {
    return value.toDate().toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  }

  if (value === undefined || value === null || value === '') {
    return <Text c="dimmed" size="sm">-</Text>;
  }

  return value as ReactNode;
};

const isLikelyStockCompanyRegistry = (registryData: CompanyRegistryData) => {
  const registryTypeText = [
    registryData.license_type_desc,
    registryData.legal_structure_desc,
  ].filter(Boolean).join(' ').toLowerCase();

  return registryTypeText.includes('stock') || registryTypeText.includes('مساهم');
};

const getLegacyBeneficiaryOwnedShares = (
  beneficiary: CompanyRegistryBeneficiary,
  registryData: CompanyRegistryData,
) => {
  if (!beneficiary.owner_shares) {
    return undefined;
  }

  const ownerSharesUnit = beneficiary.owner_shares_unit ?? (isLikelyStockCompanyRegistry(registryData) ? 'percent' : 'shares');
  return ownerSharesUnit === 'shares' ? beneficiary.owner_shares : undefined;
};

const getLegacyBeneficiaryOwnershipPercentage = (
  beneficiary: CompanyRegistryBeneficiary,
  registryData: CompanyRegistryData,
) => {
  if (!beneficiary.owner_shares) {
    return undefined;
  }

  const ownerSharesUnit = beneficiary.owner_shares_unit ?? (isLikelyStockCompanyRegistry(registryData) ? 'percent' : 'shares');
  return ownerSharesUnit === 'percent' ? beneficiary.owner_shares : undefined;
};

const displayPercentageValue = (value: unknown): ReactNode => {
  const displayableValue = displayValue(value);
  if (displayableValue !== value) {
    return displayableValue;
  }
  const valueText = String(value).trim();
  return valueText.includes('%') ? valueText : `${valueText}%`;
};

const getBeneficiaryOwnedShares = (beneficiary: CompanyRegistryBeneficiary, registryData: CompanyRegistryData) =>
  beneficiary.owned_shares ?? getLegacyBeneficiaryOwnedShares(beneficiary, registryData);

const getBeneficiaryOwnershipPercentage = (beneficiary: CompanyRegistryBeneficiary, registryData: CompanyRegistryData) =>
  beneficiary.ownership_percentage ?? getLegacyBeneficiaryOwnershipPercentage(beneficiary, registryData);

const getTimestampDate = (value: unknown): Date | null => {
  return value instanceof Timestamp ? value.toDate() : null;
};

const cleanFundNumber = (value: number) => Math.abs(value) < 0.0000001 ? 0 : value;

const finiteFundNumberOrZero = (value: unknown) => typeof value === 'number' && Number.isFinite(value) ? value : 0;

const normalizedFundTransactionText = (value?: string) => (value || '').trim().toLowerCase();

const getPositiveFundTransactionAmount = (transaction: CustomerFundDataTransaction) => {
  return Math.abs(finiteFundNumberOrZero(transaction.amount || transaction.user_amount));
};

const getPositiveFundTransactionShares = (transaction: CustomerFundDataTransaction) => {
  return Math.abs(finiteFundNumberOrZero(transaction.shares || transaction.user_shares));
};

const getPositiveFundTransactionFee = (transaction: CustomerFundDataTransaction) => {
  return Math.abs(finiteFundNumberOrZero(transaction.portal_fee_override ?? transaction.fees));
};

const isOpenEndedSubscriptionTransaction = (transaction: CustomerFundDataTransaction) => {
  const type = normalizedFundTransactionText(transaction.type);
  const description = normalizedFundTransactionText(transaction.description);
  return type === 'subscription'
    || type === 'subscriptions'
    || type === 'transfer in'
    || description === 'subscription'
    || description === 'subscriptions'
    || description === 'transfer in';
};

const isOpenEndedUnitRedemptionTransaction = (transaction: CustomerFundDataTransaction) => {
  const type = normalizedFundTransactionText(transaction.type);
  const description = normalizedFundTransactionText(transaction.description);
  return getPositiveFundTransactionShares(transaction) > 0
    && (
      type === 'redemption'
      || type === 'redemptions'
      || type === 'transfer out'
      || description === 'redemption'
      || description === 'redemptions'
      || description === 'transfer out'
    );
};

const calculateProfitLossPercent = (profitLoss: number, basis: number) => {
  return basis > 0 ? cleanFundNumber((profitLoss / basis) * 100) : undefined;
};

const calculateOpenEndedFundCostBasis = (
  transactionsByID?: Record<string, CustomerFundDataTransaction>,
  options?: { annotateTransactions?: boolean },
) => {
  let units = 0;
  let costBasis = 0;
  let realizedProfitLoss = 0;

  const transactions = Object.values(transactionsByID || {})
    .sort((a, b) => (a.deal_date?.toMillis() || 0) - (b.deal_date?.toMillis() || 0));

  transactions.forEach((transaction) => {
    const amount = getPositiveFundTransactionAmount(transaction);
    const shares = getPositiveFundTransactionShares(transaction);
    const fee = getPositiveFundTransactionFee(transaction);
    const description = normalizedFundTransactionText(transaction.description);
    if (options?.annotateTransactions) {
      delete transaction.profit_loss;
    }

    if (isOpenEndedSubscriptionTransaction(transaction)) {
      units += shares;
      costBasis += amount + fee;
      return;
    }

    if (description === 'roc') {
      costBasis = Math.max(costBasis - amount, 0);
      return;
    }

    if (description === 'pnl') {
      realizedProfitLoss += amount;
      return;
    }

    if (isOpenEndedUnitRedemptionTransaction(transaction)) {
      const netRedemptionAmount = Math.max(amount - fee, 0);
      const unitsBeforeRedemption = Math.max(units, 0);
      const costBasisBeforeRedemption = Math.max(costBasis, 0);
      const averageCostPerUnit = unitsBeforeRedemption > 0 ? costBasis / unitsBeforeRedemption : 0;
      const redeemedUnits = Math.min(shares, unitsBeforeRedemption);
      const redeemedCostBasis = Math.min(costBasis, redeemedUnits * averageCostPerUnit);
      const realizedProfitLossForRedemption = netRedemptionAmount - redeemedCostBasis;

      realizedProfitLoss += realizedProfitLossForRedemption;
      units = Math.max(units - shares, 0);
      costBasis = units > 0 ? Math.max(costBasis - redeemedCostBasis, 0) : 0;

      if (options?.annotateTransactions) {
        const realizedProfitLossPercent = calculateProfitLossPercent(realizedProfitLossForRedemption, redeemedCostBasis);
        transaction.profit_loss = {
          average_cost_per_unit: cleanFundNumber(averageCostPerUnit),
          redeemed_cost_basis: cleanFundNumber(redeemedCostBasis),
          realized_profit_loss: cleanFundNumber(realizedProfitLossForRedemption),
          ...(realizedProfitLossPercent !== undefined && { realized_profit_loss_percent: realizedProfitLossPercent }),
          units_before: cleanFundNumber(unitsBeforeRedemption),
          cost_basis_before: cleanFundNumber(costBasisBeforeRedemption),
          units_after: cleanFundNumber(units),
          cost_basis_after: cleanFundNumber(costBasis),
          method: 'weighted_average_cost',
        };
      }
    }
  });

  return {
    units,
    costBasis: cleanFundNumber(costBasis),
    realizedProfitLoss: cleanFundNumber(realizedProfitLoss),
  };
};

const calculateOpenEndedFundProfitLossValues = (fund: Fund | undefined, fundData: CustomerFundData) => {
  const calculation = calculateOpenEndedFundCostBasis(fundData.transactions);
  const storedEunits = finiteFundNumberOrZero(fundData.eunits);
  const units = storedEunits > 0 ? storedEunits : calculation.units;
  const nav = fund?.nav_per_unit ?? 0;
  const marketValue = fundData.market_value ?? units * nav;
  const costBasis = calculation.costBasis;
  const realizedProfitLoss = calculation.realizedProfitLoss;
  const unrealizedProfitLoss = cleanFundNumber(marketValue - costBasis);
  const totalProfitLoss = cleanFundNumber(realizedProfitLoss + unrealizedProfitLoss);

  return {
    costBasis,
    averageCostPerUnit: units > 0 ? cleanFundNumber(costBasis / units) : undefined,
    realizedProfitLoss,
    unrealizedProfitLoss,
    unrealizedProfitLossPercent: calculateProfitLossPercent(unrealizedProfitLoss, costBasis),
    totalProfitLoss,
  };
};

const getFundTransactionFeeTotal = (fundInPosition: CustomerFundData) => {
  return Object.values(fundInPosition.transactions ?? {}).reduce((sum, transaction) => {
    return sum + getPositiveFundTransactionFee(transaction);
  }, 0);
};

const getLatestFundAllocationDate = (fundData: CustomerFundData[], fundByCode: { [key: string]: Fund }) => {
  const dates: Date[] = [];

  fundData.forEach((fundInPosition) => {
    Object.values(fundInPosition.transactions ?? {}).forEach((transaction) => {
      const dealDate = getTimestampDate(transaction.deal_date);
      if (dealDate) {
        dates.push(dealDate);
      }
    });

    const profitLossNavDate = getTimestampDate(fundInPosition.profit_loss_nav_date);
    if (profitLossNavDate) {
      dates.push(profitLossNavDate);
    }

    const fund = fundByCode[fundInPosition.gl_code ?? ''];
    const navDate = getTimestampDate(fund?.nav_date);
    if (navDate) {
      dates.push(navDate);
    }

    Object.values(fund?.latest_deal_date ?? {}).forEach((latestDealDate) => {
      const dealDate = getTimestampDate(latestDealDate);
      if (dealDate) {
        dates.push(dealDate);
      }
    });

    const closedEndedStartDate = getTimestampDate(fund?.closed_ended_start_date);
    if (closedEndedStartDate) {
      dates.push(closedEndedStartDate);
    }
  });

  if (dates.length === 0) {
    return null;
  }

  return dates.reduce((latestDate, currentDate) => (
    currentDate.getTime() > latestDate.getTime() ? currentDate : latestDate
  ), dates[0]);
};

const getRegistryStatusColor = (status?: string) => {
  if (!status) {
    return 'gray';
  }

  return status === '1' ? 'green' : 'orange';
};

const renderRegistryTable = <T extends Record<string, any>>(
  rows: T[] | undefined,
  columns: { key: keyof T | string; label: string; render?: (row: T) => ReactNode }[],
  emptyText: string,
  actions?: (row: T, index: number) => ReactNode,
) => {
  if (!rows?.length) {
    return <Text c="dimmed" size="sm">{emptyText}</Text>;
  }

  return (
    <Table striped withTableBorder withColumnBorders>
      <Table.Thead>
        <Table.Tr>
          {columns.map((column) => <Table.Th key={String(column.key)}>{column.label}</Table.Th>)}
          {actions && <Table.Th w={80}>Actions</Table.Th>}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((row, index) => (
          <Table.Tr key={index}>
            {columns.map((column) => (
              <Table.Td key={String(column.key)}>
                {column.render ? column.render(row) : displayValue(row[column.key as keyof T])}
              </Table.Td>
            ))}
            {actions && (
              <Table.Td>
                {actions(row, index)}
              </Table.Td>
            )}
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
};

const CompanyRegistryPanel = ({ customerData }: { customerData: Customer }) => {
  const functions = useFunctions();
  const [refreshPending, setRefreshPending] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState<ReactNode>(null);
  const [refreshError, setRefreshError] = useState(false);
  const registryData = customerData.mociCompanyData;
  const warnings = registryData?.registry_warnings ?? [];
  const refreshCompanyRegistryData = async () => {
    setRefreshPending(true);
    setRefreshMessage(null);
    setRefreshError(false);

    try {
      const refreshCompanyRegistryFunction = httpsCallable<CompanyRegistryCustomerRefreshRequest, CompanyRegistryCustomerRefreshResult>(
        functions,
        'refreshCompanyRegistryForCustomer',
      );
      const result = await refreshCompanyRegistryFunction({
        customerID: customerData.id,
        crmID: customerData.crmID,
      });
      const resultWarnings = result.data.companyData.registry_warnings ?? [];
      setRefreshMessage(
        <>
          Company registry data refreshed from MOCI.
          {resultWarnings.length > 0 && (
            <>
              <Space h="xs" />
              {resultWarnings.map((warning) => <Text key={warning} size="sm">{warning}</Text>)}
            </>
          )}
        </>,
      );
    } catch (error: any) {
      setRefreshError(true);
      setRefreshMessage(error.message ?? 'Unable to refresh company registry data.');
    } finally {
      setRefreshPending(false);
    }
  };

  // Authorized signatories handlers
  const db = useFirestore();
  const authorizedSignatories = customerData.authorized_signatories;
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [editingSignatory, setEditingSignatory] = useState<CompanyAuthorizedSignatories | null>(null);

  const { saveSignatory, deleteSignatory, loading, error, setError } = useAuthorizedSignatories(
    customerData.id!,
    authorizedSignatories ?? [],
  );
  const handleEdit = (row: CompanyAuthorizedSignatories) => {
    setEditingSignatory(row);
    openModal();
  };

  const handleAdd = () => {
    setEditingSignatory(null);
    setError(null);
    openModal();
  };

  const [enrichedSignatories, setEnrichedSignatories] = useState<CompanyAuthorizedSignatories[]>([]);

  useEffect(() => {
    if (!authorizedSignatories?.length) {
      setEnrichedSignatories([]);
      return;
    }
    const fetchCustomers = async () => {
      const enriched = await Promise.all(
        authorizedSignatories.map(async (s) => {
          try {
            const snap = await getDoc(doc(db, 'customers', s.id));
            return {
              ...s,
              customer: snap.exists() ? (snap.data() as Customer) : undefined,
            };
          } catch (e) {
            return s;
          }
        })
      );
      console.log('Enriched signatories:', enriched.length);
      setEnrichedSignatories(enriched);
    };


    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(authorizedSignatories?.map((s) => s.id))]);


  return (
    <Accordion.Item value="companyRegistry" key="companyRegistry">
      <Accordion.Control>
        <Group>
          <Title order={4}>MOCI Company Registry</Title>
          <Badge color={getRegistryStatusColor(registryData?.license_status)}>
            {registryData?.license_status_desc || (registryData ? 'Fetched' : 'Not fetched')}
          </Badge>
          {warnings.length > 0 && <Badge color="orange">Review</Badge>}
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack>
          <Group justify="space-between" align="flex-start">
            <Text c="dimmed" size="sm">
              Refresh pulls the latest company registry data from MOCI and updates this customer directly.
            </Text>
            <Button
              leftSection={<IconRefresh size={16} />}
              loading={refreshPending}
              disabled={!customerData.commercial_register}
              onClick={refreshCompanyRegistryData}
            >
              Refresh from MOCI
            </Button>
          </Group>

          {refreshMessage && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color={refreshError ? 'red' : 'green'}
              title={refreshError ? 'Refresh failed' : 'Refresh complete'}
            >
              {refreshMessage}
            </Alert>
          )}

          {warnings.length > 0 && (
            <Alert icon={<IconAlertCircle size={16} />} color="yellow" title="Registry warning">
              {warnings.map((warning) => <Text key={warning} size="sm">{warning}</Text>)}
            </Alert>
          )}

          {!registryData ? (
            <Paper shadow="xs" p="md">
              <Text c="dimmed">No MOCI registry data has been saved for this customer yet.</Text>
              <Text c="dimmed" size="sm">
                Authorized signatories below are maintained in the CRM and can be added or edited even while MOCI is unavailable.
              </Text>
            </Paper>
          ) : (
            <>
              <SimpleGrid cols={{ base: 1, md: 2 }}>
                <CustomInfoCard
                  title="Company Details"
                  color="blue"
                  icon={<IconBuildingBank size={16} />}
                  list={[
                    { label: 'Commercial Register', value: registryData.commercial_register, noCapitalize: true },
                    { label: 'Arabic Name', value: registryData.name_ar, noCapitalize: true },
                    { label: 'English Name', value: registryData.name_en || '-', noCapitalize: true },
                    { label: 'Trade Name', value: registryData.trade_name_ar, noCapitalize: true },
                    { label: 'Central Number', value: registryData.central_number, noCapitalize: true },
                    { label: 'Company Civil ID', value: registryData.company_civil_id, noCapitalize: true },
                    { label: 'Legal Structure', value: registryData.legal_structure_desc, noCapitalize: true },
                    { label: 'License Type', value: registryData.license_type_desc, noCapitalize: true },
                    { label: 'License Target', value: registryData.license_target_desc, noCapitalize: true },
                    { label: 'Registry Status', value: <Badge color={getRegistryStatusColor(registryData.license_status)}>{registryData.license_status_desc || registryData.license_status || '-'}</Badge> },
                  ]}
                  attention={warnings.length > 0}
                />
                <CustomInfoCard
                  title="Registration and Contact"
                  color="green"
                  icon={<IconGlobe size={16} />}
                  list={[
                    { label: 'Registration Date', value: registryData.registration_date, noCapitalize: true },
                    { label: 'Incorporation Date', value: registryData.incorporation_date, noCapitalize: true },
                    { label: 'Total Capital', value: registryData.total_capital, noCapitalize: true },
                    { label: 'Total Shares', value: registryData.total_share_no, noCapitalize: true },
                    { label: 'Last Budget Status', value: registryData.last_budget_status, noCapitalize: true },
                    { label: 'Nationality', value: registryData.nationality, noCapitalize: true },
                    { label: 'Mobile', value: registryData.mobile, noCapitalize: true },
                    { label: 'Email', value: registryData.email, noCapitalize: true },
                    { label: 'Website', value: registryData.website, noCapitalize: true },
                  ]}
                />
              </SimpleGrid>

              <Paper shadow="xs" p="md">
                <Group mb="xs">
                  <ThemeIcon color="cyan" variant="light"><IconFolder size={16} /></ThemeIcon>
                  <Title order={5}>Licenses</Title>
                </Group>
                {renderRegistryTable<CompanyRegistryLicense>(registryData.licenses, [
                  { key: 'commercial_no', label: 'License No' },
                  { key: 'company_name', label: 'Company Name' },
                  { key: 'license_type_desc', label: 'Type' },
                  { key: 'registration_start_date', label: 'Start' },
                  { key: 'registration_end_date', label: 'End' },
                  { key: 'status', label: 'Status', render: (row) => <Badge color={getRegistryStatusColor(row.status)}>{row.status || '-'}</Badge> },
                ], 'No licenses returned by MOCI.')}
              </Paper>

              <Paper shadow="xs" p="md">
                <Group mb="xs">
                  <ThemeIcon color="grape" variant="light"><IconChartLine size={16} /></ThemeIcon>
                  <Title order={5}>Activities</Title>
                </Group>
                {renderRegistryTable<CompanyRegistryActivity>(registryData.activities, [
                  { key: 'activity_code', label: 'Code' },
                  { key: 'activity_desc', label: 'Activity' },
                  { key: 'sector_desc', label: 'Sector' },
                  { key: 'is_active', label: 'Active' },
                ], 'No activities returned by MOCI.')}
              </Paper>

              <Paper shadow="xs" p="md">
                <Group mb="xs">
                  <ThemeIcon color="orange" variant="light"><IconMapPin size={16} /></ThemeIcon>
                  <Title order={5}>Addresses</Title>
                </Group>
                {renderRegistryTable<CompanyRegistryAddress>(registryData.addresses, [
                  { key: 'governorate_code', label: 'Governorate' },
                  { key: 'district', label: 'District' },
                  { key: 'block', label: 'Block' },
                  { key: 'street', label: 'Street' },
                  { key: 'building_name', label: 'Building' },
                  { key: 'floor_no', label: 'Floor' },
                  { key: 'unit_no', label: 'Unit' },
                ], 'No addresses returned by MOCI.')}
              </Paper>

              <Paper shadow="xs" p="md">
                <Group mb="xs">
                  <ThemeIcon color="teal" variant="light"><IconUsers size={16} /></ThemeIcon>
                  <Title order={5}>Beneficiaries</Title>
                </Group>
                {renderRegistryTable<CompanyRegistryBeneficiary>(registryData.beneficiaries, [
                  { key: 'civil_id', label: 'Civil ID' },
                  { key: 'name', label: 'Name' },
                  { key: 'nationality', label: 'Nationality' },
                  { key: 'status', label: 'Status' },
                  { key: 'request_status', label: 'Request Status' },
                  {
                    key: 'owned_shares',
                    label: 'Owned Shares',
                    render: (row) => displayValue(getBeneficiaryOwnedShares(row, registryData)),
                  },
                  {
                    key: 'ownership_percentage',
                    label: 'Ownership %',
                    render: (row) => displayPercentageValue(getBeneficiaryOwnershipPercentage(row, registryData)),
                  },
                  { key: 'is_manager', label: 'Manager' },
                  { key: 'mobile', label: 'Mobile' },
                  { key: 'email', label: 'Email' },
                ], 'No beneficiaries returned by MOCI.')}
              </Paper>
            </>
          )}

          <Paper shadow="xs" p="md">
            <Group mb="xs" justify="space-between">
              <Group>
                <ThemeIcon color="teal" variant="light"><IconUsers size={16} /></ThemeIcon>
                <Title order={5}>Authorized Signatories</Title>
              </Group>
              <Button size="xs" leftSection={<IconPlus size={14} />} onClick={handleAdd}>
                Add
              </Button>
            </Group>

            {renderRegistryTable<CompanyAuthorizedSignatories>(
              enrichedSignatories,
              [
                { key: 'id', label: 'Customer ID' },
                { key: 'customer', label: 'Name En', render: (row) => row.customer?.name_en ?? '-' },
                { key: 'customer', label: 'Name Ar', render: (row) => row.customer?.name_ar ?? '-' },
                { key: 'customer', label: 'Civil ID', render: (row) => row.customer?.civilID ?? '-' },
                { key: 'customer', label: 'Mobile', render: (row) => row.customer?.mobile ?? '-' },
                { key: 'role_within_company', label: 'Role' },
              ],
              'No authorized signatories.',
              (row) => (
                <Group gap={4} wrap="nowrap">
                  <Tooltip label="Edit">
                    <ActionIcon variant="subtle" color="blue" onClick={() => handleEdit(row)}>
                      <IconEdit size={16} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Delete">
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => {
                        if (window.confirm('Are you sure you want to delete this signatory?')) {
                          deleteSignatory(row);
                        }
                      }}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              ),
            )}

            <SignatoryModal
              opened={modalOpened}
              onClose={closeModal}
              onSubmit={saveSignatory}
              editing={editingSignatory}
              loading={loading}
              error={error}
            />
          </Paper>
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
};

export const CustomerCompanyRegistry = ({ customerData }: { customerData: Customer }) => (
  <Accordion chevronPosition="right" variant="contained" defaultValue="companyRegistry">
    <CompanyRegistryPanel customerData={customerData} />
  </Accordion>
);

const getPortfolioAccordionValue = (portfolio: Portfolio) =>
  portfolio.code || portfolio.id || portfolio.key || portfolio.investmentPortfolio || "";

const resolveRequestedPortfolioAccordionValue = (portfoliosData: Portfolio[], requestedPortfolio?: string | null) => {
  if (!requestedPortfolio) {
    return "";
  }

  const normalizedRequestedPortfolio = requestedPortfolio.trim().toLowerCase();
  const matchedPortfolio = portfoliosData.find((portfolio) =>
    [
      portfolio.code,
      portfolio.id,
      portfolio.key,
      portfolio.investmentPortfolio,
    ].some((value) => `${value ?? ""}`.trim().toLowerCase() === normalizedRequestedPortfolio),
  );

  return matchedPortfolio ? getPortfolioAccordionValue(matchedPortfolio) : requestedPortfolio;
};

type CustomerSummaryProductType = Exclude<CustomerSummaryType, 'overview'>;

const customerSummaryNumberFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
});

const customerSummaryNumberText = (
  value: number,
  { currency = 'KWD', percent = false, brackets = false }: { currency?: string; percent?: boolean; brackets?: boolean } = {},
) => {
  const roundedValue = Math.abs(value) < 0.0005 ? 0 : value;
  const displayValue = brackets && roundedValue < 0 ? Math.abs(roundedValue) : roundedValue;
  const formatted = customerSummaryNumberFormatter.format(Number(displayValue.toFixed(3)));
  const suffix = percent ? ' %' : (currency ? ` ${currency}` : '');
  return brackets && roundedValue < 0 ? `(${formatted})${suffix}` : `${formatted}${suffix}`;
};

const CustomerSummaryNumber = ({
  value,
  currency = 'KWD',
  percent = false,
  brackets = false,
  color = false,
}: {
  value: number | null | undefined;
  currency?: string;
  percent?: boolean;
  brackets?: boolean;
  color?: boolean;
}) => {
  if (value === null || value === undefined) {
    return <Text c="dimmed" size="sm">-</Text>;
  }

  const displayColor = color ? (value > 0 ? 'green' : value < 0 ? 'red' : 'black') : 'black';
  return <Text size="sm" c={displayColor}>{customerSummaryNumberText(value, { currency, percent, brackets })}</Text>;
};

const CustomerSummaryMetric = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Group gap={6} wrap="nowrap">
    <Text size="xs" c="dimmed" fw={600}>{label}</Text>
    {children}
  </Group>
);

const CustomerSummaryPanel = ({ summary }: { summary?: CustomerSummaryDoc }) => {
  if (!summary) {
    return null;
  }

  type SummaryMetric = { label: string; value: number | null | undefined; brackets?: boolean; color?: boolean };
  const metricCandidates: Array<SummaryMetric | null> = [
    { label: 'Value', value: summary.totals.current_value },
    { label: 'Cost', value: summary.totals.cost_basis },
    { label: 'Realized', value: summary.totals.realized_profit_loss, brackets: true, color: true },
    { label: 'Unrealized', value: summary.totals.unrealized_profit_loss, brackets: true, color: true },
    { label: 'Total P/L', value: summary.totals.total_profit_loss, brackets: true, color: true },
    summary.lifetime.fees
      ? { label: 'Fees', value: summary.lifetime.fees }
      : null,
    summary.lifetime.net_cash_flow
      ? { label: 'Net cash flow', value: summary.lifetime.net_cash_flow, brackets: true, color: true }
      : null,
    summary.availability.has_ytd
      ? { label: 'YTD cash dist.', value: summary.ytd.cash_distributions }
      : null,
    summary.availability.has_ytd && summary.ytd.fees
      ? { label: 'YTD fees', value: summary.ytd.fees }
      : null,
  ];
  const metrics = metricCandidates.filter((metric): metric is SummaryMetric & { value: number } => (
    !!metric && metric.value !== null && metric.value !== undefined
  ));

  return (
    <Group gap="lg" mb="sm" wrap="wrap" align="center">
      <Badge variant="light" color="gray">As of {summary.as_of_date}</Badge>
      {metrics.map((metric) => (
        <CustomerSummaryMetric key={metric.label} label={metric.label}>
          <CustomerSummaryNumber
            value={metric.value}
            brackets={metric.brackets}
            color={metric.color}
          />
        </CustomerSummaryMetric>
      ))}
    </Group>
  );
};

export const CustomerInfo = ({ fundData, portfoliosData, customerData, kfhTradeAccountsData, customerSummariesData = [], activePortfolioCode, pendingOrders = [] }: CustomerInfoProps) => {

  //use context data
  const configContext = useContext(ConfigContext);
  const globalDataContext = useContext(GlobalDataContext);
  const appContext = useContext(MyUserDataContext);
  const presenceConfig = useContext(PresenceContext);
  const summaryByType = useMemo(() => {
    return customerSummariesData.reduce((summaries, summary) => {
      if (summary.summary_type !== 'overview') {
        summaries[summary.summary_type] = summary;
      }
      return summaries;
    }, {} as Partial<Record<CustomerSummaryProductType, CustomerSummaryDoc>>);
  }, [customerSummariesData]);


  let userClaims = appContext.firebaseTokenResult?.claims.customClaims as CustomClaims;

  // Get KFH Trade Accounts summary as a Portfolio
  // Only admins / CR are allowed to see KFH Trade Accounts data
  if (!canViewKfhTradeAccounts(userClaims)) {
    kfhTradeAccountsData = [];
  }
  const hasKFHTradeAccountBalances = hasKFHTradeBalance(kfhTradeAccountsData);
  const kfhTradeAccountsSummary = convertKfhTradeAccountsToPortfolio(kfhTradeAccountsData, configContext.exchange_rates?.rates || {});

  console.log("KFH Trade Accounts Summary:", kfhTradeAccountsSummary);

  //If it's a KFH RM user and the customer didn't consent to access RM data, show the consent page
  if (userClaims["kfh_rm"] && !customerData.agreements?.kfh_rm_access_consent) {
    return <AskForConsent customerData={customerData} />
  }



  //arange fund to be by map by code
  const fundByCode: { [key: string]: Fund } = {};
  const openEndedFundsCode: string[] = [];

  globalDataContext.funds?.map((fund) => {
    if (fund.id) {
      fundByCode[fund.id] = fund;
    }

    if (fund.code) {
      fundByCode[fund.code] = fund;
      if (fund.type == "open_ended") {
        openEndedFundsCode.push(fund.code);
      }
    }

  });

  const pendingOrdersByFundCode = buildPendingFundOrderGroups(pendingOrders, fundByCode);
  const openEndedFundData = fundData.filter((fundInPosition) => (openEndedFundsCode.includes(fundInPosition.gl_code ?? '')));
  const closeEndedFundData = fundData.filter((fundInPosition) => (!openEndedFundsCode.includes(fundInPosition.gl_code ?? '')));
  const fundDataCodes = new Set(fundData.map((fundInPosition) => fundInPosition.gl_code).filter((code): code is string => !!code));
  const pendingOnlyFundGroups = Object.values(pendingOrdersByFundCode).filter((group) =>
    !fundDataCodes.has(group.fundCode)
  );
  const openEndedPendingOnlyGroups = pendingOnlyFundGroups.filter((group) => getPendingGroupFundType(group) === 'open_ended');
  const closeEndedPendingOnlyGroups = pendingOnlyFundGroups.filter((group) => getPendingGroupFundType(group) !== 'open_ended');
  const closeEndedPendingOrdersByFundCode = Object.fromEntries(
    Object.entries(pendingOrdersByFundCode).filter(([fundCode]) => !fundDataCodes.has(fundCode))
  );

  if ((!portfoliosData || portfoliosData.length <= 0) && (!fundData || fundData.length <= 0) && pendingOnlyFundGroups.length <= 0 && (kfhTradeAccountsSummary.position?.total_capital_market_value ?? 0) <= 0) {
    return <Paper ta={'center'} p={25} ><Text>There are no records to display</Text></Paper>
  }

  let defaultAccordionValue = ""
  if (openEndedFundData.length > 0 || openEndedPendingOnlyGroups.length > 0) {
    defaultAccordionValue = "openEndedFunds";
  } else if (closeEndedFundData.length > 0 || closeEndedPendingOnlyGroups.length > 0) {
    defaultAccordionValue = "closeEndedFunds";
  } else if ((portfoliosData?.length ?? 0) > 0) {
    defaultAccordionValue = "managedPortfoliosSummary";
  } else if (hasKFHTradeAccountBalances) {
    defaultAccordionValue = "KFHTrade";
  }
  const requestedPortfolioAccordionValue = resolveRequestedPortfolioAccordionValue(portfoliosData, activePortfolioCode);
  const defaultPositionsAccordionValue = requestedPortfolioAccordionValue || defaultAccordionValue;

  const portfolioPerformanceTargets = portfoliosData.filter(portfolio => !!portfolio.id).map(portfolio => ({ subject: 'PORTFOLIO', investmentId: portfolio.id! } as InvestmentPerformanceTarget));
  const openEndedPerformanceTargets = openEndedFundData.filter(fund => !!fund.id).map(fund => ({ subject: 'FUND_HOLDING', investmentId: fund.id! } as InvestmentPerformanceTarget));

  return (
    <InvestmentPerformanceProvider
      customerId={customerData.id}
      enabled={canReadInvestmentPerformance(customerData, userClaims)}
      investments={[...portfolioPerformanceTargets, ...openEndedPerformanceTargets]}
    >
    <Accordion
      key={defaultPositionsAccordionValue || "positions"}
      chevronPosition="right"
      variant="contained"
      defaultValue={defaultPositionsAccordionValue}
    >

      {(openEndedFundData.length > 0 || openEndedPendingOnlyGroups.length > 0) &&
        <Accordion.Item value="openEndedFunds" key="openEndedFunds">
          <Accordion.Control>
            <Group wrap="nowrap">
              <Title order={4}>{"Open Ended Funds"}</Title>
              <Badge color='green'>Active</Badge>
              <Group ml="auto" mr="sm"><TotalValueSparkline investments={openEndedPerformanceTargets} /></Group>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Paper shadow="xs" p="md">
              <Space h="xs" />
              <CustomerSummaryPanel summary={summaryByType.open_ended} />
              <FundsTable fundData={openEndedFundData} fundByCode={fundByCode} configContext={configContext} type={"open_ended"} pendingOrdersByFundCode={pendingOrdersByFundCode} pendingOnlyFundGroups={openEndedPendingOnlyGroups} customerId={customerData.id} />
            </Paper>
          </Accordion.Panel>
        </Accordion.Item>
      }
      {(closeEndedFundData.length > 0 || closeEndedPendingOnlyGroups.length > 0) &&
        <Accordion.Item value="closeEndedFunds" key="closeEndedFunds">
          <Accordion.Control>
            <Group >
              <Title order={4}>{"Closed Ended Funds"}</Title>
              <Badge color='green'>Active</Badge>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Paper shadow="xs" p="md">
              <Space h="xs" />
              <CustomerSummaryPanel summary={summaryByType.closed_ended} />
              <FundsTable fundData={closeEndedFundData} fundByCode={fundByCode} configContext={configContext} type={"close_ended"} pendingOrdersByFundCode={closeEndedPendingOrdersByFundCode} pendingOnlyFundGroups={closeEndedPendingOnlyGroups} customerId={customerData.id} />
            </Paper>
          </Accordion.Panel>
        </Accordion.Item>
      }
      {(portfoliosData?.length ?? 0) > 0 &&
        <Accordion.Item value="managedPortfoliosSummary" key="managedPortfoliosSummary">
          <Accordion.Control>
            <Group >
              <Title order={4}>{"Managed Portfolios Summary"}</Title>
              <Badge color='green'>Active</Badge>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Paper shadow="xs" p="md">
              <CustomerSummaryPanel summary={summaryByType.portfolios} />
            </Paper>
          </Accordion.Panel>
        </Accordion.Item>
      }
      {portfoliosData?.map(portfolio => <PortfolioInfo key={getPortfolioAccordionValue(portfolio)} portfolio={portfolio as Portfolio} customerId={customerData.id} />)}
      {hasKFHTradeAccountBalances && <Accordion.Item value="KFHTrade" key="KFHTrade">
        <Accordion.Control>
          <Group >
            <Title order={4}>{"KFHTrade"}</Title>
            <Badge color='green'>Active</Badge>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          <Paper shadow="xs" p="md">
            <Space h="xs" />
            <CustomerSummaryPanel summary={summaryByType.kfhtrade} />
            <TradeAccounts accounts={kfhTradeAccountsData} />
          </Paper>
        </Accordion.Panel>
      </Accordion.Item>}

    </Accordion>
    </InvestmentPerformanceProvider>
  )




}


export interface PortfolioInfoProps {
  portfolio: Portfolio;
  customerId?: string;
}


// Component to display portfolio information
const PortfolioInfo = ({ portfolio, customerId }: PortfolioInfoProps) => {
  const portfolioStatementLauncherRef = useRef<GenevaReportLauncherHandle | null>(null);
  const realizedProfitLossLauncherRef = useRef<GenevaReportLauncherHandle | null>(null);

  const appContext = useContext(MyUserDataContext);
  let userClaims = appContext.firebaseTokenResult?.claims.customClaims as CustomClaims;
  const hidePerformanceForRm = !!userClaims?.kfh_rm && !show_performance_for_rm;


  // Function to get table rows for positions
  function getRows(positionType: "foreign" | "local", positions: Position[]): React.ReactNode {
    if (!positions) {
      return undefined;
    }
    positions = calculatePortfolioPercentages(positions);
    const groupedPositions = groupPositionsByExchangeAndCurrency(positions);
    let rows: JSX.Element[] = [];

    // Generate rows for each grouped position
    for (const groupedPosition of groupedPositions) {
      if (groupedPosition.exchange && groupedPosition.currency) {
        rows.push(
          <Table.Tr key={groupedPosition.title}>
            <Table.Td ta='left' colSpan={16}>{groupedPosition.title}</Table.Td>
          </Table.Tr>
        );
      }
      // Generate rows for each position within the group
      for (const element of groupedPosition.list) {
        if (element.cash) {
          rows.push(
            <Table.Tr key={element.code}>
              {positionType == "local" && <Table.Td colSpan={7}>{"Total " + element.code + " Cash"}</Table.Td>}
              {positionType == "foreign" && <>
                <Table.Td colSpan={4}>{"Total Cash In " + element.exchange}</Table.Td>
                <Table.Td></Table.Td>
                <Table.Td></Table.Td>
              </>
              }

              <Table.Td><NumberDisplay value={element.quantity} withCommas /></Table.Td>

              <Table.Td>-</Table.Td>
              {positionType == "foreign" && <>
                <Table.Td><NumberDisplay value={element.total_cost_year_close_local} withCommas /></Table.Td>
                <Table.Td><NumberDisplay value={element.total_market_value_local} withCommas /></Table.Td>
                <Table.Td><NumberDisplay value={element.unrealized_fx} withCommas withBrackets /></Table.Td>
                <Table.Td><NumberDisplay value={element.unrealized_equity} withCommas withBrackets /></Table.Td>
                <Table.Td><NumberDisplay value={element.total_unrealized_gain_loss_local} withCommas withBrackets /></Table.Td>
              </>}
              <Table.Td><NumberDisplay value={element.portfolio_percent} /></Table.Td>
            </Table.Tr>
          );
        } else {
          rows.push(
            <Table.Tr key={element.code}>
              <Table.Td>{element.code}</Table.Td>
              <Table.Td>{element.name}</Table.Td>
              <Table.Td><NumberDisplay value={element.quantity} withCommas /></Table.Td>
              <Table.Td><NumberDisplay value={element.bonus_shares_and_right_issues} withCommas /></Table.Td>
              <Table.Td><NumberDisplay value={element.average_cost} withCommas /></Table.Td>
              <Table.Td><NumberDisplay value={element.total_cost} withCommas /></Table.Td>
              <Table.Td><NumberDisplay value={element.total_market_value} withCommas /></Table.Td>
              <Table.Td><NumberDisplay value={element.unrealized_gain_loss} withCommas withBrackets withColor /></Table.Td>
              <Table.Td><NumberDisplay value={element.gain_loss_percent} withCommas withBrackets withColor withPercent /></Table.Td>
              {positionType == "foreign" && <>
                <Table.Td><NumberDisplay value={element.total_cost_year_close_local} withCommas /></Table.Td>
                <Table.Td><NumberDisplay value={element.total_market_value_local} withCommas /></Table.Td>
                <Table.Td><NumberDisplay value={element.unrealized_fx} withCommas withBrackets withColor /></Table.Td>
                <Table.Td><NumberDisplay value={element.unrealized_equity} withCommas withBrackets withColor /></Table.Td>
                <Table.Td><NumberDisplay value={element.total_unrealized_gain_loss_local} withCommas withBrackets withColor /></Table.Td>
              </>}
              <Table.Td><NumberDisplay value={element.portfolio_percent} /></Table.Td>
            </Table.Tr>
          );
        }

      }
      // Add total securities row if there are positions
      if ((groupedPosition.list?.length ?? 0) > 0 && groupedPosition.combinedPosition.cash == false) {
        rows.push(
          <Table.Tr key={groupedPosition.title + "2"}>
            <Table.Td colSpan={4}>{"Total Securities in " + groupedPosition.exchange}</Table.Td>

            <Table.Td></Table.Td>
            <Table.Td><NumberDisplay value={groupedPosition.combinedPosition.total_cost} withCommas /></Table.Td>
            <Table.Td><NumberDisplay value={groupedPosition.combinedPosition.total_market_value} withCommas /></Table.Td>
            <Table.Td><NumberDisplay value={groupedPosition.combinedPosition.unrealized_gain_loss} withCommas withBrackets withColor /></Table.Td>
            <Table.Td><NumberDisplay value={groupedPosition.combinedPosition.gain_loss_percent} withCommas withBrackets withColor withPercent /></Table.Td>
            {positionType == "foreign" && <>
              <Table.Td><NumberDisplay value={groupedPosition.combinedPosition.total_cost_year_close_local} withCommas /></Table.Td>
              <Table.Td><NumberDisplay value={groupedPosition.combinedPosition.total_market_value_local} withCommas /></Table.Td>
              <Table.Td><NumberDisplay value={groupedPosition.combinedPosition.unrealized_fx} withCommas withBrackets withColor /></Table.Td>
              <Table.Td><NumberDisplay value={groupedPosition.combinedPosition.unrealized_equity} withCommas withBrackets withColor /></Table.Td>
              <Table.Td><NumberDisplay value={groupedPosition.combinedPosition.total_unrealized_gain_loss_local} withCommas withBrackets withColor /></Table.Td>
            </>}

            <Table.Td><NumberDisplay value={groupedPosition.combinedPosition.portfolio_percent} /></Table.Td>
          </Table.Tr>
        );
      }
    }

    return rows;
  }

  // Function to get the positions table for a given type of position (foreign/local)
  function getPositionsTable(positionType: "foreign" | "local", positions: Position[]) {
    return <Paper shadow="xs" p="md">
      <Group>
        <ThemeIcon color="blue" variant="light">
          {<IconGlobe size={16} />}
        </ThemeIcon>
        <Title order={5}>
        </Title>
        {positionType == "foreign" ? "Foreign Position" : "Local Position" as ReactNode}
      </Group>
      <Space h="xs" />
      <Table striped withTableBorder withColumnBorders ta="center" >
        <Table.Thead>
          <Table.Tr >
            <Table.Th ta="center" >Sec. <br />No</Table.Th>
            <Table.Th ta="center" >Security <br />Name</Table.Th>
            <Table.Th ta="center" >Quantity</Table.Th>
            <Table.Th ta="center" >Bonus <br />Shares + <br />Right Issues</Table.Th>
            <Table.Th ta="center" >Avg <br />Cost</Table.Th>
            <Table.Th ta="center" >Total Cost</Table.Th>
            <Table.Th ta="center" >Total <br />Market Value</Table.Th>
            <Table.Th ta="center" >UR G/(L)</Table.Th>
            <Table.Th ta="center" >% G/(L)</Table.Th>
            {positionType == 'foreign' && <>
              <Table.Th ta="center" >Total Cost <br />(Year Close)</Table.Th>
              <Table.Th ta="center" >Total <br />Market Value</Table.Th>
              <Table.Th ta="center" >UR <br />FX</Table.Th>
              <Table.Th ta="center" >UR <br />Equity</Table.Th>
              <Table.Th ta="center" >Total UR <br />G/(L)</Table.Th>
            </>}
            <Table.Th ta="center" >%<br />Portfolio</Table.Th>
          </Table.Tr>
          {positionType == 'foreign' && <>
            <Table.Tr>
              <Table.Th colSpan={4}></Table.Th>
              <Table.Th colSpan={5} ta="center">Foreign Currency</Table.Th>
              <Table.Th colSpan={5} ta="center">Local Currency</Table.Th>
              <Table.Th colSpan={1}></Table.Th>
            </Table.Tr>
          </>}
        </Table.Thead>
        <Table.Tbody>
          {
            getRows(positionType, positions)
          }
        </Table.Tbody>
      </Table>
    </Paper>
  }


  return (
    <>
      <Accordion.Item value={getPortfolioAccordionValue(portfolio)} key={getPortfolioAccordionValue(portfolio)}>
        <Accordion.Control>
          <Group wrap="nowrap">
            <Title order={4}>{portfolio.code}</Title>
            <Badge color={portfolio.status === 'Active' ? 'green' : 'orange'}>{portfolio.status}</Badge>
            {portfolio.id && <Group ml="auto" mr="sm"><TotalValueSparkline investments={[{ subject: 'PORTFOLIO', investmentId: portfolio.id }]} /></Group>}
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          <Group justify="flex-end" mb="md">
            <GenevaReportLauncherButton
              ref={portfolioStatementLauncherRef}
              portfolio={portfolio}
              customerId={customerId}
              reportName="cashtran_ssrs"
              reportTitle="Portfolio Statement"
              description={({ portfolioIdentifier }) => `Queue a Geneva portfolio statement for ${portfolioIdentifier}.`}
              fields={portfolioStatementFields}
              renderTrigger={() => null}
              validate={({ values }) => {
                const startDate = getDateFieldValue(values, 'startDate');
                const endDate = getDateFieldValue(values, 'endDate');

                if (!startDate || !endDate) {
                  return 'Select both a start date and an end date.';
                }

                if (startDate > endDate) {
                  return 'The start date must be on or before the end date.';
                }

                return undefined;
              }}
              buildParameters={({ portfolioIdentifier, values }): Record<string, string> => {
                const startDate = getDateFieldValue(values, 'startDate');
                const endDate = getDateFieldValue(values, 'endDate');

                if (!startDate || !endDate) {
                  return {};
                }

                return {
                  Portfolio: portfolioIdentifier,
                  PortfolioDescription: portfolio.name || portfolio.name_en || portfolioIdentifier,
                  PeriodStartDate: formatGenevaDateTime(startDate, false),
                  PeriodEndDate: formatGenevaDateTime(endDate, true),
                  KnowledgeDate: getTodayEndOfDay(),
                  BookCurrency: 'KWD',
                  TradeSettle: 'Settle',
                  IncludeFX: '0',
                };
              }}
            />

            <GenevaReportLauncherButton
              ref={realizedProfitLossLauncherRef}
              portfolio={portfolio}
              customerId={customerId}
              reportName="K117931G_glmap_realizedgl"
              reportTitle="Realized Profit / (Loss)"
              description={({ portfolioIdentifier }) => `Queue a Geneva realized profit / loss report for ${portfolioIdentifier}.`}
              fields={realizedProfitLossFields}
              renderTrigger={() => null}
              validate={({ values }) => {
                const startDate = getDateFieldValue(values, 'startDate');
                const endDate = getDateFieldValue(values, 'endDate');

                if (!startDate || !endDate) {
                  return 'Select both a start date and an end date.';
                }

                if (startDate > endDate) {
                  return 'The start date must be on or before the end date.';
                }

                return undefined;
              }}
              buildParameters={({ portfolioIdentifier, values }): Record<string, string> => {
                const startDate = getDateFieldValue(values, 'startDate');
                const endDate = getDateFieldValue(values, 'endDate');

                if (!startDate || !endDate) {
                  return {};
                }

                const knowledgeDate = getCurrentKnowledgeDateTime();

                return {
                  Portfolio: portfolioIdentifier,
                  PortfolioDescription: portfolio.name || portfolio.name_en || portfolioIdentifier,
                  PeriodStartDate: formatGenevaDateTime(startDate, false),
                  PeriodEndDate: formatGenevaDateTime(endDate, true),
                  KnowledgeDate: knowledgeDate,
                  PriorKnowledgeDate: knowledgeDate,
                  Group1: 'InvestmentType',
                  TransactionToPrint: 'Sell',
                  ShowOnlySell: '1',
                  StyleName: 'e',
                  AddendumPages: 'Short',
                  ReportPeriodEndDate: REPORT_PERIOD_END_DATE,
                  NettingRuleView: '0',
                  Consolidate: 'None',
                  AccountingRunType: 'Dynamic',
                  GLMapSet: 'Standard Geneva GL Map',
                };
              }}
            />

            <Menu shadow="md" width={260} position="bottom-end" withinPortal>
              <Menu.Target>
                <Button
                  variant="light"
                  size="xs"
                  rightSection={<IconChevronDown size={14} />}
                >
                  Reports
                </Button>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>Geneva reports</Menu.Label>
                <Menu.Item onClick={() => portfolioStatementLauncherRef.current?.openModal()}>
                  Portfolio Statement
                </Menu.Item>
                <Menu.Item onClick={() => realizedProfitLossLauncherRef.current?.openModal()}>
                  Realized Profit / (Loss)
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
          <InvestmentPerformancePanel subject="PORTFOLIO" investmentId={portfolio.id} />
          <SimpleGrid cols={3}>
            <CustomInfoCard
              title="Portfolio Details"
              color="blue"
              icon={<IconFolder size={16} />}
              list={getPortfolioDetailsList(portfolio)}
            />
            <CustomInfoCard
              title="Position Financials"
              color="yellow"
              icon={<IconCash size={16} />}
              list={getPositionFinancialsList(portfolio)}
            />
            {hidePerformanceForRm ? (
              <Paper shadow="xs" p="md">
                <Group wrap="nowrap" align="flex-start">
                  <ThemeIcon color="green" variant="light">
                    <IconChartLine size={16} />
                  </ThemeIcon>
                  <Title order={5}>Performance Metrics and Income</Title>
                </Group>
                <Space h="xs" />
                <Text size="sm" c="dimmed">
                  Not available now but will be added in the future.
                </Text>
              </Paper>
            ) : (
              <CustomInfoCard
                title="Performance Metrics and Income"
                color="green"
                icon={<IconChartLine size={16} />}
                list={getPerformanceMetricsList(portfolio)}
              />
            )}
          </SimpleGrid>
          {portfolio.position?.local_position &&
            <>
              <br />
              {getPositionsTable('local', portfolio.position.local_position)}
            </>
          }
          {portfolio.position?.foreign_position &&
            <>
              <br />
              {getPositionsTable('foreign', portfolio.position.foreign_position)}
            </>
          }
        </Accordion.Panel>
      </Accordion.Item>
    </>
  )
}

// Component to display funds position table

/**
 * FundsTable component calculates and displays fund data in a table format.
 * It includes calculations for total value, Year-To-Date (YTD) distributions, and total distributions,
 * with a breakdown of Dividends (PNL) and Return on Capital (ROC).
 * Tooltips show detailed breakdowns when hovering over distributions.
 * 
 * Props:
 * - fundData: Array of customer fund data.
 * - fundByCode: Object mapping fund codes to detailed fund information.
 * - configContext: Configuration data including exchange rates.
 */
const FundsTable = ({
  fundData,
  fundByCode,
  configContext,
  type,
  pendingOrdersByFundCode = {},
  pendingOnlyFundGroups = [],
  customerId,
}: {
  fundData: CustomerFundData[],
  fundByCode: { [key: string]: Fund },
  configContext: ConfigDataType,
  type: "close_ended" | "open_ended",
  pendingOrdersByFundCode?: Record<string, PendingFundOrderGroup>,
  pendingOnlyFundGroups?: PendingFundOrderGroup[],
  customerId?: string,
}) => {

  const [openedCode, setOpenedCode] = useState<string | null>();
  const latestAllocationDate = getLatestFundAllocationDate(fundData, fundByCode);

  // Aggregated totals
  let totalValue = 0;
  let totalDividends = 0; // total PNL
  let totalReturnOnCapital = 0; // total ROC
  let totalYTDDividends = 0; // total YTD PNL
  let totalYTDReturnOnCapital = 0; // total YTD ROC
  let totalRemainingValue = 0; // total Remaining (for closed-ended funds)
  let totalFees = 0; // total open-ended fees
  let totalCostBasis = 0; // total open-ended cost basis
  let totalRealizedProfitLoss = 0; // total open-ended realized P/L
  let totalUnrealizedProfitLoss = 0; // total open-ended unrealized P/L
  let totalProfitLoss = 0; // total open-ended P/L
  let totalUnrealizedProfitLossPercent: number | undefined; // total open-ended unrealized P/L %

  // Storing per-fund totals in KWD
  const fundsTotalInKWD: Record<string, number> = {};
  const fundsDividendsInKWD: Record<string, number> = {}; // PNL
  const fundsReturnOnCapitalInKWD: Record<string, number> = {}; // ROC
  const fundsYTDDividendsInKWD: Record<string, number> = {}; // YTD PNL
  const fundsYTDReturnOnCapitalInKWD: Record<string, number> = {}; // YTD ROC
  const fundsRemainingInKWD: Record<string, number> = {}; // Remaining
  const fundsFeesInKWD: Record<string, number> = {}; // Fees
  const fundsCostBasisInKWD: Record<string, number> = {}; // Cost basis
  const fundsRealizedProfitLossInKWD: Record<string, number> = {}; // Realized P/L
  const fundsUnrealizedProfitLossInKWD: Record<string, number> = {}; // Unrealized P/L
  const fundsProfitLossInKWD: Record<string, number> = {}; // Total P/L
  const pieData: PieValueType[] = [];
  const colors: string[] = [
    "#335B74",
    "#1CADE4",
    "#2683C6",
    "#27CED7",
    "#42BA97",
    "#3E8853",
    "#62A39F",
    "#6EAC1C",
    "#3EBA53",
    "#285C73",  // Additional color
    "#90D3E5",  // Additional color
    "#107BBF",  // Additional color
    "#12C3B8",  // Additional color
    "#5EC587",  // Additional color
    "#46A367",  // Additional color
  ];

  // Function to convert any currency amount to KWD using exchange rates
  const convertToKWD = (amount: number, currency: string) => {
    if (currency !== "KWD") {
      const rate = parseFloat(configContext.exchange_rates?.rates[currency] ?? '1');
      return amount * rate;
    }
    return amount;
  };

  const getFundProfitLossValues = (fundInPosition: CustomerFundData, fund?: Fund) => {
    if (fund?.type === 'open_ended' && fundInPosition.transactions && Object.keys(fundInPosition.transactions).length > 0) {
      return calculateOpenEndedFundProfitLossValues(fund, fundInPosition);
    }

    const realizedProfitLoss = fundInPosition.realized_profit_loss;
    const unrealizedProfitLoss = fundInPosition.unrealized_profit_loss;
    const costBasis = fundInPosition.cost_basis;
    const unrealizedProfitLossPercent = fundInPosition.unrealized_profit_loss_percent
      ?? (unrealizedProfitLoss !== undefined && costBasis && costBasis > 0
        ? (unrealizedProfitLoss / costBasis) * 100
        : undefined);
    const totalProfitLoss = fundInPosition.total_profit_loss
      ?? (realizedProfitLoss !== undefined && unrealizedProfitLoss !== undefined
        ? realizedProfitLoss + unrealizedProfitLoss
        : undefined);

    return {
      costBasis,
      averageCostPerUnit: fundInPosition.average_cost_per_unit,
      realizedProfitLoss,
      unrealizedProfitLoss,
      unrealizedProfitLossPercent,
      totalProfitLoss,
    };
  };

  // Calculate values for each fund, converting currencies to KWD as necessary
  const calculateFundValues = (fundInPosition: CustomerFundData) => {
    const fund = fundByCode[fundInPosition.gl_code ?? ''];
    const isOpenEnded = fund?.type === 'open_ended';

    const nav = isOpenEnded ? fund?.nav_per_unit ?? 0 : 0;
    const units = fundInPosition.eunits ?? 0;
    const totalSubscriptions = fundInPosition.total_subscriptions ?? 0;
    const profitLoss = getFundProfitLossValues(fundInPosition, fund);
    const totalFees = getFundTransactionFeeTotal(fundInPosition);
    const totalFeesInKWD = Object.values(fundInPosition.transactions ?? {}).reduce((sum, transaction) => {
      return sum + convertToKWD(getPositiveFundTransactionFee(transaction), transaction.currency || fundInPosition.currency || 'KWD');
    }, 0);

    // Calculate total value in KWD
    const fundTotalKWD = isOpenEnded ? fundInPosition.market_value ?? units * nav : totalSubscriptions;

    const currency = fundInPosition.currency ?? 'KWD';

    // Return calculated values for both distributions and YTD distributions
    return {
      totalKWD: convertToKWD(fundTotalKWD, currency),
      totalDividendsInKWD: convertToKWD((fundInPosition.total_redemptions?.pnl ?? 0), currency), // PNL
      totalReturnOnCapitalInKWD: convertToKWD((fundInPosition.total_redemptions?.roc ?? 0), currency), // ROC
      totalYTDDividendsInKWD: convertToKWD((fundInPosition.ytd_redemptions?.pnl ?? 0), currency), // YTD PNL
      totalYTDReturnOnCapitalInKWD: convertToKWD((fundInPosition.ytd_redemptions?.roc ?? 0), currency), // YTD ROC
      totalRemainingKWD: convertToKWD(totalSubscriptions - (fundInPosition.total_redemptions?.roc ?? 0), currency), // remaining value
      totalFees,
      totalFeesInKWD,
      costBasisInKWD: convertToKWD(profitLoss.costBasis ?? 0, currency),
      realizedProfitLossInKWD: convertToKWD(profitLoss.realizedProfitLoss ?? 0, currency),
      unrealizedProfitLossInKWD: convertToKWD(profitLoss.unrealizedProfitLoss ?? 0, currency),
      totalProfitLossInKWD: convertToKWD(profitLoss.totalProfitLoss ?? 0, currency),
    };
  };

  // Iterate over each fund in fundData and calculate totals for each
  if (fundData.length > 0) {
    fundData.forEach(fundInPosition => {
      const { totalKWD, totalDividendsInKWD, totalReturnOnCapitalInKWD, totalYTDDividendsInKWD, totalYTDReturnOnCapitalInKWD, totalRemainingKWD, totalFeesInKWD, costBasisInKWD, realizedProfitLossInKWD, unrealizedProfitLossInKWD, totalProfitLossInKWD } = calculateFundValues(fundInPosition);

      const code = fundInPosition.gl_code ?? '';
      fundsTotalInKWD[code] = totalKWD;
      fundsDividendsInKWD[code] = totalDividendsInKWD; // PNL
      fundsReturnOnCapitalInKWD[code] = totalReturnOnCapitalInKWD; // ROC
      fundsYTDDividendsInKWD[code] = totalYTDDividendsInKWD; // YTD PNL
      fundsYTDReturnOnCapitalInKWD[code] = totalYTDReturnOnCapitalInKWD; // YTD ROC
      fundsRemainingInKWD[code] = totalRemainingKWD; // Remaining
      fundsFeesInKWD[code] = totalFeesInKWD; // Fees
      fundsCostBasisInKWD[code] = costBasisInKWD; // Cost basis
      fundsRealizedProfitLossInKWD[code] = realizedProfitLossInKWD; // Realized P/L
      fundsUnrealizedProfitLossInKWD[code] = unrealizedProfitLossInKWD; // Unrealized P/L
      fundsProfitLossInKWD[code] = totalProfitLossInKWD; // Total P/L
    });

    // Calculate grand totals
    totalValue = Object.values(fundsTotalInKWD).reduce((sum, value) => sum + value, 0);
    totalDividends = Object.values(fundsDividendsInKWD).reduce((sum, value) => sum + value, 0);
    totalReturnOnCapital = Object.values(fundsReturnOnCapitalInKWD).reduce((sum, value) => sum + value, 0);
    totalYTDDividends = Object.values(fundsYTDDividendsInKWD).reduce((sum, value) => sum + value, 0);
    totalYTDReturnOnCapital = Object.values(fundsYTDReturnOnCapitalInKWD).reduce((sum, value) => sum + value, 0);
    totalRemainingValue = Object.values(fundsRemainingInKWD).reduce((sum, value) => sum + value, 0);
    totalFees = Object.values(fundsFeesInKWD).reduce((sum, value) => sum + value, 0);
    totalCostBasis = Object.values(fundsCostBasisInKWD).reduce((sum, value) => sum + value, 0);
    totalRealizedProfitLoss = Object.values(fundsRealizedProfitLossInKWD).reduce((sum, value) => sum + value, 0);
    totalUnrealizedProfitLoss = Object.values(fundsUnrealizedProfitLossInKWD).reduce((sum, value) => sum + value, 0);
    totalProfitLoss = Object.values(fundsProfitLossInKWD).reduce((sum, value) => sum + value, 0);
    totalUnrealizedProfitLossPercent = totalCostBasis > 0 ? (totalUnrealizedProfitLoss / totalCostBasis) * 100 : undefined;

    let counter = 0;

    fundData.forEach(fundInPosition => {
      const code = fundInPosition.gl_code ?? '';
      const name = fundInPosition.gl_name ?? '';
      let percentage: number;
      if (type === "close_ended" && totalRemainingValue !== 0) {
        percentage = (fundsRemainingInKWD[code] / totalRemainingValue) * 100;
      } else {
        percentage = (fundsTotalInKWD[code] / totalValue) * 100;
      }
      let color = counter < colors.length ? colors[counter] : undefined;
      percentage = parseFloat(percentage.toFixed(2))

      pieData.push(
        {
          id: counter,
          value: percentage,
          label: (location) => location == 'legend' ? name + ' (' + percentage + '%)' : name,
          ...(color && { color }),
        }
      );
      counter++;
    });
  }


  const tableColumnCount = type === "open_ended" ? 15 : 10;
  const renderBlankCells = (count: number, keyPrefix: string) => Array.from({ length: count }, (_, index) => (
    <Table.Td key={`${keyPrefix}-${index}`}><Text c="dimmed" size="sm">-</Text></Table.Td>
  ));

  const renderPendingOnlyFundRow = (group: PendingFundOrderGroup) => {
    const code = group.fundCode;
    const openedPendingCode = `pending:${code}`;
    const totalKWD = convertToKWD(group.pendingAmount, group.currency);
    const shares = group.pendingUnits > 0 ? group.pendingUnits : undefined;

    return (
      <React.Fragment key={openedPendingCode}>
        <Table.Tr>
          <Table.Td>
            <IconChevronDown className='icon-hover' style={{ transition: '0.2s', rotate: openedCode == openedPendingCode ? '180deg' : '0deg' }} onClick={() => (openedCode == openedPendingCode ? setOpenedCode(null) : setOpenedCode(openedPendingCode))} />
          </Table.Td>
          <Table.Td>
            <Stack gap={4} align="center">
              {code && fundByCode[code] ? (
                <Tooltip transitionProps={{ transition: 'pop', duration: 200 }} label={<FundSummarizedData fund={fundByCode[code]} />}>
                  <span>{group.fundName}</span>
                </Tooltip>
              ) : (
                <span>{group.fundName}</span>
              )}
              <PendingPositionMarker group={group} />
              <FundConfirmationLetterLink customerId={customerId} fundId={group.fund?.id ?? group.fundCode} />
            </Stack>
          </Table.Td>
          <Table.Td><NumberDisplay value={shares} withCommas /></Table.Td>
          <Table.Td><NumberDisplay value={group.pendingAmount} withCommas currency={group.currency} /></Table.Td>
          <Table.Td><NumberDisplay value={totalKWD} withCommas currency="KWD" /></Table.Td>
          {renderBlankCells(tableColumnCount - 5, openedPendingCode)}
        </Table.Tr>
        <Table.Tr key={`${openedPendingCode}Orders`} style={{ height: 0, padding: 0, border: 0 }} >
          <Table.Td colSpan={tableColumnCount} style={{ height: 0, padding: 0, border: 0 }} >
            <Collapse expanded={openedCode == openedPendingCode} transitionDuration={200}>
              <Card style={{ width: '100%' }}>
                <PendingFundOrdersTable orders={group.orders} />
              </Card>
            </Collapse>
          </Table.Td>
        </Table.Tr>
      </React.Fragment>
    );
  };

  // Render the table with fund data and totals
  return (
    <>
      <Title fz={'h3'} ta={'center'}>Fund Allocation Breakdown</Title>
      {latestAllocationDate && (
        <Text size="sm" c="dimmed" ta="center" mt={4}>As of {dateFormater(latestAllocationDate)}</Text>
      )}
      {pieData.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              flex: '1 1 350px', // grow, shrink, and minimum basis
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '1rem',
            }}
          >
            <FundsAllocationPieChart pieData={pieData} totalValue={type === "close_ended" ? totalRemainingValue : totalValue} />
          </div>
          {/* Used another component for legend to make it responsive as the package legend overlap and it not responsive */}
          <Legend
            items={pieData}
            style={{
              flex: '1 1 300px',
            }}
          />
        </div>
      )}
      {pieData.length > 0 && <br />}
      <Table striped withTableBorder withColumnBorders ta="center">
        <Table.Thead>
          <Table.Tr>
            <Table.Th ta="center"></Table.Th>
            <Table.Th ta="center">Fund Name</Table.Th>
            <Table.Th ta="center">Units</Table.Th>
            <Table.Th ta="center">{type == "open_ended" ? "" : "Initial "}Value (Fund Currency)</Table.Th>
            <Table.Th ta="center">{type == "open_ended" ? "Market " : "Initial "}Value (KWD)</Table.Th>
            <Table.Th ta="center">% of Total {type == "open_ended" ? "" : "Remaining "} Value</Table.Th>
            <Table.Th ta="center">YTD Distributions</Table.Th>
            <Table.Th ta="center">Total Distributions</Table.Th>
            {type === "open_ended" && <Table.Th ta="center">Fees</Table.Th>}
            {type === "close_ended" && <Table.Th ta="center">Total ROC</Table.Th>}
            {type === "close_ended" && <Table.Th ta="center">Remaining Value</Table.Th>}
            {type === "open_ended" && <Table.Th ta="center">Avg Cost</Table.Th>}
            {type === "open_ended" && <Table.Th ta="center">Cost Basis</Table.Th>}
            {type === "open_ended" && <Table.Th ta="center">Realized P/L</Table.Th>}
            {type === "open_ended" && <Table.Th ta="center">Unrealized P/L</Table.Th>}
            {type === "open_ended" && <Table.Th ta="center">Unrealized P/L %</Table.Th>}
            {type === "open_ended" && <Table.Th ta="center">Total P/L</Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {fundData.map(fundInPosition => {
            const code = fundInPosition.gl_code ?? '';
            const pendingGroup = pendingOrdersByFundCode[code];
            const hasPendingOrders = (pendingGroup?.orders.length ?? 0) > 0;
            const { totalKWD, totalDividendsInKWD, totalReturnOnCapitalInKWD, totalYTDDividendsInKWD, totalYTDReturnOnCapitalInKWD, totalRemainingKWD, totalFees } = calculateFundValues(fundInPosition);

            const shares = Math.round(fundInPosition.eunits ?? 0);
            const amount = fundByCode[code]?.type === 'open_ended'
              ? fundInPosition.market_value ?? (fundInPosition.eunits ?? 0) * (fundByCode[code]?.nav_per_unit ?? 0)
              : fundInPosition.total_subscriptions ?? 0;
            const totalYTDDividends = fundInPosition.ytd_redemptions?.pnl ?? 0;
            const totalYTDReturnOnCapital = fundInPosition.ytd_redemptions?.roc ?? 0;
            const totalDividends = fundInPosition.total_redemptions?.pnl ?? 0;
            const totalReturnOnCapital = fundInPosition.total_redemptions?.roc ?? 0;
            const totalDistributions = fundInPosition.total_redemptions?.total ?? 0;
            const totalYTDDistributions = fundInPosition.ytd_redemptions?.total ?? 0;
            const remainingValue = amount - totalReturnOnCapital;
            const profitLoss = getFundProfitLossValues(fundInPosition, fundByCode[code]);
            // if type is open_ended, fund percentage is based on totalKWD/totalValue
            // if type is close_ended, fund percentage is based on totalRemainingKWD/totalRemainingValue
            let fundPercentage = (totalKWD / totalValue) * 100;
            if (type === "close_ended" && totalRemainingValue !== 0) {
              fundPercentage = (totalRemainingKWD / totalRemainingValue) * 100;
            }
            fundPercentage = parseFloat(fundPercentage.toFixed(2))
            let transactions: CustomerFundDataTransaction[] = []
            if (fundInPosition.transactions) {
              const transactionsByID: Record<string, CustomerFundDataTransaction> = {};
              Object.entries(fundInPosition.transactions ?? {}).forEach(([key, transactionRow]) => {
                const transaction = { ...transactionRow };
                if (transaction.currency !== "KWD" && transaction.currency) {
                  let rate = parseFloat(configContext.exchange_rates?.rates[transaction.currency] ?? 0);
                  transaction.amountInKWD = ((transaction.amount ?? 0) * rate);
                } else {
                  transaction.amountInKWD = transaction.amount;
                }
                transactionsByID[key] = transaction;
              });
              if (type === "open_ended") {
                calculateOpenEndedFundCostBasis(transactionsByID, { annotateTransactions: true });
              }
              transactions = Object.values(transactionsByID);
              transactions = transactions.sort((a, b) => (b.deal_date?.toMillis() ?? 0) - (a.deal_date?.toMillis() ?? 0));
            }

            return (<React.Fragment key={code}>
              <Table.Tr key={code}>
                <Table.Td>
                  <IconChevronDown className='icon-hover' style={{ transition: '0.2s', rotate: openedCode == code ? '180deg' : '0deg' }} onClick={() => (openedCode == code ? setOpenedCode(null) : setOpenedCode(code))} />
                </Table.Td>

                <Table.Td>
                  <Stack gap={4} align="center">
                    {code && fundByCode[code] ? (
                      <Tooltip transitionProps={{ transition: 'pop', duration: 200 }} label={<FundSummarizedData fund={fundByCode[code]} />}>
                        <span>{fundInPosition.gl_name}</span>
                      </Tooltip>
                    ) : (
                      <span>{fundInPosition.gl_name}</span>
                    )}
                    {pendingGroup && <PendingPositionMarker group={pendingGroup} />}
                    <FundConfirmationLetterLink customerId={customerId} fundId={fundByCode[code]?.id ?? code} />
                    {type === "open_ended" && <InvestmentPerformancePanel subject="FUND_HOLDING" investmentId={fundInPosition.id} compact />}
                  </Stack>
                </Table.Td>
                <Table.Td ><NumberDisplay value={shares} withCommas /></Table.Td>
                <Table.Td><NumberDisplay value={amount} withCommas currency={fundInPosition.currency ?? 'KWD'} /></Table.Td>
                <Table.Td><NumberDisplay value={totalKWD} withCommas currency="KWD" /></Table.Td>
                <Table.Td>
                  <NumberDisplay value={fundPercentage} withCommas withPercent />

                </Table.Td>
                {/* YTD Distributions Tooltip */}
                <Table.Td>
                  <DistributionData pnl={totalYTDDividends} roc={totalYTDReturnOnCapital} total={totalYTDDistributions} currency={fundInPosition.currency ?? 'KWD'} />
                </Table.Td>

                {/* Total Distributions Tooltip */}
                <Table.Td>
                  <DistributionData pnl={totalDividends} roc={totalReturnOnCapital} total={totalDistributions} currency={fundInPosition.currency ?? 'KWD'} />
                </Table.Td>
                {type === "open_ended" && <Table.Td><NumberDisplay value={totalFees} withCommas currency={fundInPosition.currency ?? 'KWD'} /></Table.Td>}
                {type === "close_ended" && <Table.Td><NumberDisplay value={totalReturnOnCapital} withCommas currency={fundInPosition.currency ?? 'KWD'} /></Table.Td>}
                {type === "close_ended" && <Table.Td><NumberDisplay value={remainingValue} withCommas currency={fundInPosition.currency ?? 'KWD'} /></Table.Td>}
                {type === "open_ended" && <Table.Td><NumberDisplay value={profitLoss.averageCostPerUnit} withCommas currency={fundInPosition.currency ?? 'KWD'} /></Table.Td>}
                {type === "open_ended" && <Table.Td><NumberDisplay value={profitLoss.costBasis} withCommas currency={fundInPosition.currency ?? 'KWD'} /></Table.Td>}
                {type === "open_ended" && <Table.Td><NumberDisplay value={profitLoss.realizedProfitLoss} withCommas withBrackets withColor currency={fundInPosition.currency ?? 'KWD'} /></Table.Td>}
                {type === "open_ended" && <Table.Td><NumberDisplay value={profitLoss.unrealizedProfitLoss} withCommas withBrackets withColor currency={fundInPosition.currency ?? 'KWD'} /></Table.Td>}
                {type === "open_ended" && <Table.Td><NumberDisplay value={profitLoss.unrealizedProfitLossPercent} withCommas withBrackets withColor withPercent /></Table.Td>}
                {type === "open_ended" && <Table.Td><NumberDisplay value={profitLoss.totalProfitLoss} withCommas withBrackets withColor currency={fundInPosition.currency ?? 'KWD'} /></Table.Td>}

              </Table.Tr>

              <Table.Tr display={'none'}></Table.Tr>
              <Table.Tr key={code + 'Transactions'} style={{ height: 0, padding: 0, border: 0 }} >
                <Table.Td colSpan={tableColumnCount} style={{ height: 0, padding: 0, border: 0 }} >
                  <Collapse expanded={openedCode == code} transitionDuration={200}>
                    <Card style={{ width: '100%' }}>
                      {type === "open_ended" && openedCode === code && <InvestmentPerformancePanel subject="FUND_HOLDING" investmentId={fundInPosition.id} />}
                      {pendingGroup && <PendingFundOrdersTable orders={pendingGroup.orders} />}
                      {hasPendingOrders && transactions.length > 0 && <Space h="md" />}
                      {(transactions.length > 0 || !hasPendingOrders) && <FundTransactions transactions={transactions} />}
                    </Card>
                  </Collapse>
                </Table.Td>
              </Table.Tr>
            </React.Fragment>
            );
          })}
          {pendingOnlyFundGroups.map(renderPendingOnlyFundRow)}
          {type === "open_ended" && fundData.length > 0 &&
            <Table.Tr key="total">
              <Table.Td colSpan={4}><Text size="sm" fw="bold">Total</Text></Table.Td>
              <Table.Td><NumberDisplay value={totalValue} withCommas currency="KWD" /></Table.Td>
              <Table.Td><NumberDisplay value={totalValue ? 100 : 0} withCommas withPercent /></Table.Td>
              <Table.Td>-</Table.Td>
              <Table.Td>-</Table.Td>
              <Table.Td><NumberDisplay value={totalFees} withCommas currency="KWD" /></Table.Td>
              <Table.Td>-</Table.Td>
              <Table.Td><NumberDisplay value={totalCostBasis} withCommas currency="KWD" /></Table.Td>
              <Table.Td><NumberDisplay value={totalRealizedProfitLoss} withCommas withBrackets withColor currency="KWD" /></Table.Td>
              <Table.Td><NumberDisplay value={totalUnrealizedProfitLoss} withCommas withBrackets withColor currency="KWD" /></Table.Td>
              <Table.Td><NumberDisplay value={totalUnrealizedProfitLossPercent} withCommas withBrackets withColor withPercent /></Table.Td>
              <Table.Td><NumberDisplay value={totalProfitLoss} withCommas withBrackets withColor currency="KWD" /></Table.Td>
            </Table.Tr>
          }
          {/* <Table.Tr key="total" >
            <Table.Td colSpan={4}><Text size="sm" fw="bold">Total</Text></Table.Td>
            <Table.Td><NumberDisplay value={totalValue} withCommas currency="KWD" /></Table.Td>
            <Table.Td>
              <NumberDisplay value={100} withCommas withPercent />
            </Table.Td>

            <Table.Td>
              -
            </Table.Td>

            <Table.Td>
              -
            </Table.Td>

          </Table.Tr> */}
        </Table.Tbody>
      </Table>
    </>
  );
};

/**
 * DistributionBreakdown component shows the breakdown of Dividends (PNL) and Return on Capital (ROC) in a tooltip.
 * Props:
 * - pnl: Amount of Dividends (PNL) in KWD.
 * - roc: Amount of Return on Capital (ROC) in KWD.
 * - currency: The currency of the values displayed.
 */
const DistributionData = ({ pnl, roc, total, currency }: { pnl: number, roc: number, total: number, currency: string }) => (
  <>{pnl === 0 && roc === 0
    ? <span>-</span>
    : (
      <Tooltip transitionProps={{ transition: 'pop', duration: 200 }} label={<DistributionBreakdown pnl={pnl} roc={roc} currency={currency} />}>
        <span><NumberDisplay value={total} withCommas currency={currency} /></span>
      </Tooltip>
    )
  }
  </>
);
const DistributionBreakdown = ({ pnl, roc, currency }: { pnl: number, roc: number, currency: string }) => (
  <Table withTableBorder={false} withColumnBorders={false} withRowBorders={false}>
    <Table.Tr>
      <Table.Td><strong>Dividends</strong></Table.Td>
      <Table.Td ta={'center'}>{<NumberDisplay value={pnl} withCommas currency={currency} customColor='white' />}</Table.Td>
    </Table.Tr>
    <Table.Tr>
      <Table.Td><strong>Return of capital</strong></Table.Td>
      <Table.Td ta={'center'}>{<NumberDisplay value={roc} withCommas currency={currency} customColor='white' />}</Table.Td>
    </Table.Tr>
  </Table>
);


// Function to calculate portfolio percentages
const calculatePortfolioPercentages = (items: Position[]): Position[] => {
  const totalMarketValue = items.reduce((acc, item) => acc + (item.total_market_value ?? 0), 0);
  return items.map((item) => ({
    ...item,
    portfolio_percent: totalMarketValue === 0 ? 0 : ((item.total_market_value ?? 0) / totalMarketValue) * 100
  }));
};

// Function to group positions by exchange and currency
const groupPositionsByExchangeAndCurrency = (positions: Position[]): GroupedPosition[] => {
  const groupedMap = new Map<string, GroupedPosition>();

  for (const position of positions) {
    const key = `${position.exchange}-${position.currency}`;

    if (!groupedMap.has(key)) {
      groupedMap.set(key, {
        exchange: position.exchange ?? "",
        currency: position.currency ?? "",
        title: `${position.exchange} (${position.currency})`,
        list: [position],
        combinedPosition: { ...position }
      });
    } else {
      const groupedPosition = groupedMap.get(key)!;
      groupedPosition.list.push(position);
      combinePositions(groupedPosition.combinedPosition, position);
    }
  }

  return Array.from(groupedMap.values());
};

// Function to combine positions for summary
const combinePositions = (combined: Position, position: Position) => {
  combined.total_cost = (combined.total_cost ?? 0) + (position.total_cost ?? 0);
  combined.total_market_value = (combined.total_market_value ?? 0) + (position.total_market_value ?? 0);
  combined.unrealized_gain_loss = (combined.unrealized_gain_loss ?? 0) + (position.unrealized_gain_loss ?? 0);
  combined.gain_loss_percent = calculateProfitLossPercent(
    combined.unrealized_gain_loss,
    combined.total_cost,
  );
  combined.total_cost_year_close_local = (combined.total_cost_year_close_local ?? 0) + (position.total_cost_year_close_local ?? 0);
  combined.total_market_value_local = (combined.total_market_value_local ?? 0) + (position.total_market_value_local ?? 0);
  combined.unrealized_fx = (combined.unrealized_fx ?? 0) + (position.unrealized_fx ?? 0);
  combined.unrealized_equity = (combined.unrealized_equity ?? 0) + (position.unrealized_equity ?? 0);
  combined.total_unrealized_gain_loss_local = (combined.total_unrealized_gain_loss_local ?? 0) + (position.total_unrealized_gain_loss_local ?? 0);
  combined.portfolio_percent = (combined.portfolio_percent ?? 0) + (position.portfolio_percent ?? 0);
};

// Functions to get various details for portfolio info cards
const getPortfolioDetailsList = (portfolio: Portfolio) => [
  { label: "PF Name", value: portfolio.investmentPortfolio },
  { label: "PF Number", value: portfolio.code },
  { label: "Opening Date", value: portfolio.position?.created },
  { label: "Managed By", value: portfolio.position?.managed_by },
  { label: "PF Manager", value: portfolio.position?.manager },
  { label: "PF Type", value: portfolio.position?.type },
  { label: "Mng Fees %", value: portfolio.position?.fees ? `${portfolio.position?.fees} %` : undefined },
  { label: "PF Status", value: portfolio.position?.status },
  { label: "Investment Portfolio", value: portfolio.investmentPortfolio },
  { label: "Portfolio Type", value: portfolio.portfolioTypeCode },
  { label: "Risk Profile", value: portfolio.riskProfile }
];

const getPositionFinancialsList = (portfolio: Portfolio) => [
  { label: "Cash", value: <NumberDisplay value={portfolio.position?.cash} withCommas /> },
  { label: "Securities", value: <NumberDisplay value={portfolio.position?.securities} withCommas /> },
  { label: "Dividend Due", value: <NumberDisplay value={portfolio.position?.dividend_due} withCommas /> },
  { label: "Sukuk Profit Receivable", value: <NumberDisplay value={portfolio.position?.sukuk_profit_receivable} withCommas /> },
  { label: "Other Receivables", value: portfolio.position?.type },
  { label: "Other Liabilities", value: <NumberDisplay value={portfolio.position?.other_liabilities} withCommas withColor withBrackets /> },
  { label: "Total Capital Market Value", value: <NumberDisplay value={portfolio.position?.total_capital_market_value} withCommas /> },
  { label: "Cash %", value: <NumberDisplay value={portfolio.position?.cash_percent} withCommas withPercent /> }
];

const getPerformanceMetricsList = (portfolio: Portfolio) => [
  { label: "Realized G/L YTD", value: <NumberDisplay value={portfolio.position?.realized_gain_loss_ytd} withCommas /> },
  { label: "Cash Dividend YTD", value: <NumberDisplay value={portfolio.position?.cash_dividend_ytd} withCommas /> },
  { label: "Unrealized P/L", value: <NumberDisplay value={portfolio.position?.unrealized_profit_loss} withCommas withBrackets withColor /> },
  { label: "Other Income", value: <NumberDisplay value={portfolio.position?.other_income} withCommas /> },
  { label: "Management Fee", value: <NumberDisplay value={portfolio.position?.management_fee} withCommas withBrackets withColor /> },
  { label: "Other Expenses", value: <NumberDisplay value={portfolio.position?.other_expenses} withCommas withColor withBrackets /> },
  { label: "Net Income", value: <NumberDisplay value={portfolio.position?.net_income} withCommas withBrackets withColor /> },
  { label: "Performance", value: <NumberDisplay value={portfolio.position?.performance} withCommas withPercent withColor /> }
];

const FundsAllocationPieChart = ({ pieData, totalValue }: { pieData: PieValueType[], totalValue: number }) => (
  <PieChart
    series={[
      {
        data: pieData,
        innerRadius: 100,
        highlightScope: { fade: 'global', highlight: 'item' },
        faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
        valueFormatter: (item: { value: number }) => `${item.value}%`,
      },
    ]}
    slotProps={{
      legend: {
        direction: 'column',
        position: { vertical: 'middle', horizontal: 'right' },
        padding: 0,
        hidden: true,
      },

    }}
    height={300}
  >
    <PieCenterLabel>
      {toKwdInt(totalValue)}
    </PieCenterLabel>

  </PieChart>
);

const PendingFundOrdersTable = ({ orders }: { orders: Order[] }) => {
  const sortedOrders = [...orders].sort((a, b) => {
    const leftExpectedNav = getPendingOrderExpectedNavDate(a)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    const rightExpectedNav = getPendingOrderExpectedNavDate(b)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    return leftExpectedNav - rightExpectedNav;
  });

  return (
    <Stack gap="xs">
      <Text size="sm" fw="bold">Pending orders</Text>
      <Table striped withTableBorder withColumnBorders ta="center">
        <Table.Tbody>
          <Table.Tr bg={'#f8f9fa'}>
            <Table.Th ta="center">Order</Table.Th>
            <Table.Th ta="center">Status</Table.Th>
            <Table.Th ta="center">Expected NAV</Table.Th>
            <Table.Th ta="center">Amount</Table.Th>
            <Table.Th ta="center">Estimated Units</Table.Th>
          </Table.Tr>
          {sortedOrders.map((order, index) => {
            const status = order.tags?.orderStatus;
            const amount = getPendingOrderAmount(order);
            const units = getPendingOrderUnits(order);
            const currency = getPendingOrderCurrency(order);

            return (
              <Table.Tr key={order.id || `${getOrderFundCode(order)}-${index}`}>
                <Table.Td>{order.id || '-'}</Table.Td>
                <Table.Td>
                  <Badge color={status?.badgeColor || 'indigo'} variant="light">{status?.title || 'Processing'}</Badge>
                </Table.Td>
                <Table.Td>{formatOrderExpectedNavDate(order)}</Table.Td>
                <Table.Td><NumberDisplay value={amount} withCommas currency={currency} /></Table.Td>
                <Table.Td><NumberDisplay value={units > 0 ? units : undefined} withCommas /></Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};

const FundTransactions = ({ transactions }: { transactions: CustomerFundDataTransaction[] }) => {
  const hasTransfers = transactions.some(isFundTransfer);
  const hasTransactionFees = transactions.some((transaction) => getPositiveFundTransactionFee(transaction) > 0);
  const hasTransactionProfitLoss = transactions.some((transaction) => transaction.profit_loss?.realized_profit_loss !== undefined);
  const hasTransactionProfitLossPercent = transactions.some((transaction) => transaction.profit_loss?.realized_profit_loss_percent !== undefined);

  return <>
    {transactions.length > 0 && <Table striped withTableBorder withColumnBorders ta="center" >
      <Table.Tbody>
        <Table.Tr key={transactions[0].code + 'Transactions'} bg={'#f8f9fa'} >
          <Table.Th ta="center">Transaction Date</Table.Th>
          <Table.Th ta="center">Type</Table.Th>
          {hasTransfers && <Table.Th ta="center">Transfer details</Table.Th>}
          {/* <Table.Th ta="center">Description</Table.Th> */}
          <Table.Th ta="center">Amount</Table.Th>
          {hasTransactionFees && <Table.Th ta="center">Fees</Table.Th>}
          <Table.Th ta="center">Units</Table.Th>
          {hasTransactionProfitLoss && <Table.Th ta="center">Realized P/L</Table.Th>}
          {hasTransactionProfitLossPercent && <Table.Th ta="center">Realized P/L %</Table.Th>}
          {/* <Table.Th ta="center">Amount (KWD)</Table.Th> */}
        </Table.Tr>
        {transactions.map(transaction => {
          //set the type and icon based on the type in fund_data where Subscriptions mean Outgoing and Redemptions mean Incoming
          //outgoing only mean subscription
          //incoming mean redemption, distributions or return on capitals
          let type = transaction.type ?? '-';
          // //capitalize the type
          // type = type.charAt(0).toUpperCase() + type.slice(1);

          // const icon = type === 'Outgoing' ? <IconArrowUp size={16} /> : type === 'Incoming' ? <IconArrowDown size={16} /> : undefined;
          let desc = transaction.description ?? type
          if (desc.toLocaleLowerCase() === 'pnl') {
            desc = 'Dividends';
          } else if (desc.toLocaleLowerCase() === 'roc') {
            desc = 'Return of Capital';
          } else if (desc.toLocaleLowerCase() === 'redemption') {
            desc = 'Redemption';
          } else if (desc.toLocaleLowerCase() === 'subscription') {
            desc = 'Subscription';
          }
          else {
            if (desc.length > 1) {
              desc = desc.charAt(0).toUpperCase() + desc.slice(1);
            }
          }

          let amount = transaction.user_amount || transaction.amount || 0;
          let fees = getPositiveFundTransactionFee(transaction);
          let shares = transaction.user_shares || transaction.shares || 0;

          return <Table.Tr >
            <Table.Td>{transaction.deal_date?.toDate().toLocaleDateString("en-gb") ?? "-"}</Table.Td>
            {/* <Table.Td>{type} {icon}</Table.Td> */}
            <Table.Td>{desc}</Table.Td>
            {hasTransfers && <Table.Td><FundTransferDetails transaction={transaction} /></Table.Td>}
            <Table.Td><NumberDisplay value={amount} withCommas currency={transaction.currency} /> </Table.Td>
            {hasTransactionFees && <Table.Td><NumberDisplay value={fees} withCommas currency={transaction.currency} /> </Table.Td>}
            <Table.Td><NumberDisplay value={shares} withCommas /> </Table.Td>
            {hasTransactionProfitLoss && <Table.Td><NumberDisplay value={transaction.profit_loss?.realized_profit_loss} withCommas withBrackets withColor currency={transaction.currency} /> </Table.Td>}
            {hasTransactionProfitLossPercent && <Table.Td><NumberDisplay value={transaction.profit_loss?.realized_profit_loss_percent} withCommas withBrackets withColor withPercent /> </Table.Td>}
            {/* <Table.Td><NumberDisplay value={transaction.amountInKWD} withCommas currency='KWD' /> </Table.Td> */}
          </Table.Tr>
        })}
      </Table.Tbody>
    </Table>}
    {transactions.length <= 0 &&
      <Text>No Transaction Found</Text>
    }
  </>
};

const StyledText = styled('text')(({ theme }) => ({
  fill: theme.palette.text.primary,
  textAnchor: 'middle',
  dominantBaseline: 'central',
}));

function PieCenterLabel({ children }: { children: React.ReactNode }) {
  const { width, height, left, top } = useDrawingArea();
  return (
    <StyledText x={left + width / 2} y={top + height / 2}>
      {children}
    </StyledText>
  );
}

const Legend = ({ items, style }: { items: PieValueType[], style: React.CSSProperties }) => {
  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      ...style,
    }}>
      {items.map((item, index) => (
        <div
          key={index}
          style={{
            display: 'flex',
            alignItems: 'center',
            marginRight: '1rem',
            marginBottom: '0.5rem',
          }}
        >
          <span
            style={{
              display: 'inline-block',
              width: '12px',
              height: '12px',
              backgroundColor: item.color,
              marginRight: '0.5rem',
            }}
          />
          <span>
            {typeof item.label === 'function'
              ? item.label('legend')
              : item.label}
          </span>
        </div>
      ))}
    </div>
  );
};
